import { Button } from "primereact/button";
import "primereact/resources/primereact.min.css";
import "primereact/resources/themes/fluent-light/theme.css";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import "./notifications.css";
import SubMenu from "./SubMenu";
import { API } from "../../utils/constants";

const MOBILE_BP = 640;
const ALL = "All";


function SkeletonCard() {
  return (
    <article className="exam-card exam-card--skeleton" aria-hidden="true">
      <div className="exam-card__logo skeleton-box" />
      <div className="exam-card__body">
        <div className="skeleton-line skeleton-line--title" />
        <div className="skeleton-line skeleton-line--short" />
        <div className="skeleton-line skeleton-line--btn" />
      </div>
    </article>
  );
}

function SkeletonGroup() {
  return (
    <section className="exam-notif-group">
      <div className="skeleton-line skeleton-line--heading" />
      <div className="exam-notif-grid">
        {[0, 1, 2].map((i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    </section>
  );
}


export default function Notification({ isMobile, headerTheme }) {
  const iconOnly = Boolean(isMobile || headerTheme);

  const [open, setOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(ALL);
  const [query, setQuery] = useState("");

  // API state
  const [examData, setExamData] = useState([]); // grouped: [{category, exams:[]}]
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false); // fetch only once per mount

  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);

  const [coords, setCoords] = useState(null);


  useEffect(() => {
    if (!open || fetched) return;

    let cancelled = false;
    setLoading(true);

    fetch(`${API}/exam-notifications`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((payload) => {
        if (cancelled) return;
        if (payload?.groups?.length) {
          setExamData(
            payload.groups.map((g) => ({
              ...g,
              exams: (g.exams || []).filter((e) => e.isActive !== false),
            }))
          );
        }
      })
      .catch(() => {
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setFetched(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, fetched]);


  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;

    if (window.innerWidth <= MOBILE_BP) {
      setCoords(null);
      return;
    }

    const r = el.getBoundingClientRect();
    const PANEL_W = Math.min(420, window.innerWidth * 0.94);
    const GAP = 10;
    const MARGIN = 16;
    const CAP = window.innerHeight * 0.78;
    let left = r.right - PANEL_W;
    left = Math.max(8, Math.min(left, window.innerWidth - PANEL_W - 8));

    const spaceBelow = window.innerHeight - r.bottom - GAP - MARGIN;
    const spaceAbove = r.top - GAP - MARGIN;

    let top;
    let maxHeight;
    if (spaceBelow >= spaceAbove) {
      top = r.bottom + GAP;
      maxHeight = Math.min(CAP, spaceBelow);
    } else {
      maxHeight = Math.min(CAP, spaceAbove);
      top = Math.max(MARGIN, r.top - GAP - maxHeight);
    }

    setCoords({ top, left, width: PANEL_W, maxHeight: Math.max(200, maxHeight) });
  }, []);


  const categories = useMemo(
    () => [ALL, ...examData.map((g) => g.category)],
    [examData]
  );

  const visibleGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return examData
      .filter((g) => activeCategory === ALL || g.category === activeCategory)
      .map((g) => ({
        ...g,
        exams: q
          ? g.exams.filter(
              (e) =>
                e.examName.toLowerCase().includes(q) ||
                g.category.toLowerCase().includes(q)
            )
          : g.exams,
      }))
      .filter((g) => g.exams.length > 0);
  }, [activeCategory, query, examData]);

  const totalVisible = useMemo(
    () => visibleGroups.reduce((sum, g) => sum + g.exams.length, 0),
    [visibleGroups]
  );

  const close = () => setOpen(false);


  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointer = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        close();
      }
    };
    const onKey = (e) => e.key === "Escape" && close();

    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    const isSheet = window.innerWidth <= MOBILE_BP;
    const prevOverflow = document.body.style.overflow;
    if (isSheet) document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      if (isSheet) document.body.style.overflow = prevOverflow;
    };
  }, [open, updatePosition]);


  const headerBtnClass =
    headerTheme === "dark"
      ? "notification-header-btn--dark"
      : headerTheme === "light"
        ? "notification-header-btn--light"
        : "";

  return (
    <div className="notification-trigger" ref={triggerRef}>
      <Button
        label={iconOnly ? null : "Exam Notifications"}
        icon="pi pi-bell"
        onClick={() => setOpen((v) => !v)}
        aria-label="Exam notifications"
        aria-expanded={open}
        className={`animated-bell rounded-full ${
          iconOnly ? "p-button-icon-only" : ""
        } ${headerBtnClass}`.trim()}
      />

      {open &&
        createPortal(
          <>
            <div className="exam-notif-backdrop" onClick={close} aria-hidden="true" />

            <section
              className={`exam-notif-panel ${coords ? "" : "exam-notif-panel--sheet"}`}
              ref={panelRef}
              role="dialog"
              aria-modal="true"
              aria-label="Exam Notifications"
              style={
                coords
                  ? {
                      top: coords.top,
                      left: coords.left,
                      width: coords.width,
                      maxHeight: coords.maxHeight,
                    }
                  : undefined
              }
            >
              <header className="exam-notif-head">
                <div className="exam-notif-head__title">
                  <span className="exam-notif-head__icon">
                    <i className="pi pi-bell" />
                  </span>
                  <div>
                    <h2>Exam Notifications</h2>
                    {loading ? (
                      <p>Loading…</p>
                    ) : (
                      <p>{totalVisible} exam{totalVisible === 1 ? "" : "s"} shown</p>
                    )}
                  </div>
                </div>
                <button
                  className="exam-notif-close"
                  onClick={close}
                  aria-label="Close notifications"
                >
                  <i className="pi pi-times" />
                </button>
              </header>

              <div className="exam-notif-search">
                <i className="pi pi-search" />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search exams or categories…"
                  aria-label="Search exams"
                />
                {query && (
                  <button
                    className="exam-notif-search__clear"
                    onClick={() => {
                      setQuery("");
                      searchRef.current?.focus();
                    }}
                    aria-label="Clear search"
                  >
                    <i className="pi pi-times" />
                  </button>
                )}
              </div>

              {!loading && categories.length > 1 && (
                <nav className="exam-notif-cats" aria-label="Categories">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      className={`exam-notif-chip ${activeCategory === cat ? "is-active" : ""}`}
                      onClick={() => setActiveCategory(cat)}
                    >
                      {cat}
                    </button>
                  ))}
                </nav>
              )}

              <div className="exam-notif-body">
                {loading ? (
                  <>
                    <SkeletonGroup />
                    <SkeletonGroup />
                  </>
                ) : visibleGroups.length === 0 ? (
                  <div className="exam-notif-empty">
                    <i className="pi pi-inbox" />
                    <p>
                      {fetched
                        ? "No exams match your search."
                        : "Could not load exam notifications."}
                    </p>
                  </div>
                ) : (
                  visibleGroups.map((group) => (
                    <section className="exam-notif-group" key={group.category}>
                      <h3 className="exam-notif-group__title">
                        {group.category}
                        <span>{group.exams.length}</span>
                      </h3>
                      <div className="exam-notif-grid">
                        {group.exams.map((exam, i) => (
                          <SubMenu key={`${exam.examName}-${i}`} {...exam} />
                        ))}
                      </div>
                    </section>
                  ))
                )}
              </div>
            </section>
          </>,
          document.body
        )}
    </div>
  );
}
